import { IResourceComponentsProps, useTranslate, useGetIdentity } from '@pankod/refine-core';
import React, { useState } from 'react';
import {
  Create,
  Form,
  Input,
  InputNumber,
  Select,
  useForm,
  Row,
  Col,
  Checkbox,
  Button,
} from '@pankod/refine-antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import 'react-mde/lib/styles/css/react-mde-all.css';
import { User } from 'interfaces';
import { UserRoleEnum } from 'types/users/userRole';
import { EnumToArray } from 'util/parseData';

export const UserCreate: React.FC<IResourceComponentsProps> = () => {
  const { formProps, saveButtonProps, queryResult } = useForm<User>();
  const t = useTranslate();
  const { data: userIdentity } = useGetIdentity<{ id: number; role: UserRoleEnum }>(); // Récupère les infos de l'utilisateur connecté, incluant l'ID

  const [newUserPhone, setNewUserPhone] = useState<string>();
  const [newUserEmail, setNewUserEmail] = useState<string>();

  const generaPassword = () => {
    formProps?.form?.setFieldsValue({ password: Math.random().toString(36).slice(-8) });
  };

  const newUserForm = () => {
    setNewUserEmail(formProps?.form?.getFieldValue('email'));
    setNewUserPhone(formProps?.form?.getFieldValue('phone'));
  };

  const onFinish = (values: any) => {
    // Ajoute parent_user_id aux valeurs soumises
    const payload = {
      ...values,
      parent_user_id: userIdentity?.id, // Inclut l'ID de l'utilisateur connecté
    };
    formProps?.onFinish?.(payload); // Appelle la fonction onFinish de useForm avec le payload modifié
  };

  // Filtrer les rôles disponibles selon le rôle de l'utilisateur connecté
  const availableRoles = userIdentity?.role === UserRoleEnum.Admin
    ? EnumToArray(UserRoleEnum) // Admins peuvent sélectionner tous les rôles
    : EnumToArray(UserRoleEnum).filter(
        (role) => role.value !== UserRoleEnum.Admin && role.value !== UserRoleEnum.Agency
      ); // Non-admins ne peuvent pas sélectionner ADMIN ou AGENCE

  return (
    <Create saveButtonProps={{ ...saveButtonProps, onClick: () => formProps?.form?.submit() }}>
      <Form
        {...formProps}
        onFinish={onFinish} // Utilise la fonction onFinish personnalisée
        initialValues={{ newsletter: false }}
        onFieldsChange={newUserForm}
        name="time_related_controls"
        layout="vertical"
      >
        <Row>
          <Col className="gutter-row" span={24}>
            <Form.Item name="newsletter" valuePropName="checked" noStyle>
              <Checkbox>{t('fields.newsletter', { ns: 'users' })}</Checkbox>
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={8} xl={8}>
            <Form.Item
              label={t('fields.lastname', { ns: 'users' })}
              name="lastname"
              rules={[{ required: true, message: 'Please input lastname' }]}
            >
              <Input maxLength={255} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={8} xl={8}>
            <Form.Item
              label={t('fields.firstname', { ns: 'users' })}
              name="firstname"
              rules={[{ required: true, message: 'Please input firstname' }]}
            >
              <Input maxLength={255} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={8}>
            <Form.Item
              label={t('fields.role', { ns: 'users' })}
              name="role"
              rules={[{ required: true }]}
            >
              <Select options={availableRoles} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={8} xl={8}>
            <Form.Item
              label={t('fields.email', { ns: 'users' })}
              name="email"
              rules={[
                { type: 'email', message: 'The input is not valid E-mail!' },
                { required: !newUserPhone, message: 'Please input email' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={8} xl={8}>
            <Form.Item
              label={t('fields.phone', { ns: 'users' })}
              name="phone"
              rules={[{ required: !newUserEmail, message: 'Please input phone' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={24} md={24} lg={8} xl={8}>
            <Form.Item
              label={t('fields.language', { ns: 'users' })}
              name="language"
              rules={[{ required: true, message: 'Please input language' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={12} md={12} lg={8} xl={8}>
            <Form.Item
              label={t('fields.total_votes', { ns: 'users' })}
              name="total_votes"
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={12} md={12} lg={8} xl={8}>
            <Form.Item
              label={t('fields.ok_votes', { ns: 'users' })}
              name="ok_votes"
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col className="gutter-row" xs={24} sm={12} md={12} lg={8} xl={8}>
            <Form.Item
              label={t('fields.password', { ns: 'users' })}
              rules={[{ required: true }]}
              shouldUpdate={(prevValues, curValues) => prevValues.password !== curValues.password}
            >
              {() => (
                <Row>
                  <Col xs={21} sm={21} md={20} lg={20} xl={21}>
                    <Form.Item
                      name="password"
                      rules={[{ required: true, message: 'Please input password' }]}
                    >
                      <Input.Password
                        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={1} sm={1} md={2} lg={2} xl={1}>
                    <Button onClick={generaPassword}>Genera</Button>
                  </Col>
                </Row>
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Create>
  );
};